"use client";

import React from "react";
import Link from "next/link";
import { 
  Stethoscope, 
  Sparkles, 
  ArrowRight, 
  ShieldCheck, 
  Activity, 
  CheckCircle2, 
  Award,
  KanbanSquare,
  Eye
} from "lucide-react";

interface DentalHeroProps {
  onOpenBooking: () => void;
  onExploreTreatments: () => void;
  currency: "INR" | "USD";
}

export default function DentalHero({
  onOpenBooking,
  onExploreTreatments,
  currency,
}: DentalHeroProps) {
  return (
    <div className="relative overflow-hidden pt-12 pb-16 lg:pt-20 lg:pb-24 border-b border-slate-800/80 bg-slate-950 text-white">
      {/* Background Ambient Glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[650px] h-[320px] bg-gradient-to-r from-teal-500/10 via-cyan-500/10 to-transparent blur-3xl pointer-events-none rounded-full" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto space-y-6">
          {/* Top Pill */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-900 border border-slate-800 text-slate-300 text-xs font-semibold shadow-inner">
            <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
            <span className="text-teal-400 font-bold uppercase tracking-wider text-[10px]">
              Aesthetica Dental Institute
            </span>
            <span className="text-slate-600">•</span>
            <span className="text-slate-400">100% Digital Dentistry &amp; Implant Center</span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-6xl font-black font-heading tracking-tight leading-[1.08] text-white">
            World-Class Dental Artistry Meets{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-teal-400 via-cyan-300 to-emerald-400">
              3D Computer-Guided Surgery.
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-sm sm:text-base text-slate-300 leading-relaxed max-w-2xl mx-auto font-normal">
            A state-of-the-art ambulatory dental surgical facility. Painless single-tooth computer anesthesia, photorealistic iTero 3D intraoral scanning, and precision hand-layered E.max ceramic smile transformations.
          </p>

          {/* Hero CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3.5 pt-2">
            <button
              onClick={onOpenBooking}
              className="w-full sm:w-auto px-7 py-3.5 rounded-xl bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-400 hover:to-teal-500 text-slate-950 font-black text-xs tracking-wide uppercase transition-all shadow-xl shadow-teal-500/20 hover:scale-[1.02] flex items-center justify-center gap-2"
            >
              <Stethoscope className="w-4 h-4 text-slate-950" />
              <span>Book Clinical 3D Consultation</span>
            </button>

            <button
              onClick={onExploreTreatments}
              className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-slate-900/90 hover:bg-slate-800 border border-slate-800 text-slate-200 hover:text-white font-bold text-xs transition-colors flex items-center justify-center gap-2"
            >
              <span>Explore Specialty Procedures</span>
              <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
            </button>
          </div>

          {/* Stat Pillars */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-10 border-t border-slate-800/80 text-left">
            <div className="p-3.5 rounded-xl bg-slate-900/50 border border-slate-800/80 space-y-1">
              <div className="flex items-center gap-1.5 text-teal-400 text-xs font-bold">
                <Activity className="w-4 h-4" />
                <span>iTero Lumina 3D</span>
              </div>
              <p className="text-[11px] text-slate-400">6,000 frames/sec zero-putty optical digital impressions</p>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-900/50 border border-slate-800/80 space-y-1">
              <div className="flex items-center gap-1.5 text-cyan-400 text-xs font-bold">
                <ShieldCheck className="w-4 h-4" />
                <span>Painless STA Wand</span>
              </div>
              <p className="text-[11px] text-slate-400">Computerized anesthesia without collateral facial numbness</p>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-900/50 border border-slate-800/80 space-y-1">
              <div className="flex items-center gap-1.5 text-emerald-400 text-xs font-bold">
                <Award className="w-4 h-4" />
                <span>10-Yr Guarantee</span>
              </div>
              <p className="text-[11px] text-slate-400">Clinical ceramic warranty &amp; fracture replacement certificate</p>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-900/50 border border-slate-800/80 space-y-1">
              <div className="flex items-center gap-1.5 text-teal-400 text-xs font-bold">
                <Eye className="w-4 h-4" />
                <span>25x Zeiss Optics</span>
              </div>
              <p className="text-[11px] text-slate-400">German microscope surgical precision and margin seal</p>
            </div>
          </div>
        </div>

        {/* Real-time CRM Sync Bridge Banner */}
        <div className="mt-12 p-4 rounded-2xl bg-gradient-to-r from-slate-900 via-slate-900/90 to-slate-900 border border-teal-500/30 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-500/30 flex items-center justify-center text-teal-400 shrink-0">
              <KanbanSquare className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-xs sm:text-sm font-bold text-white flex items-center gap-2">
                <span>Interactive Healthcare Patient Pipeline Active</span>
                <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
              </h4>
              <p className="text-[11px] text-slate-400">
                Book a consultation above: watch it flow live into our 6-stage Clinic Floor &amp; EHR CRM with automated PDF passes.
              </p>
            </div>
          </div>

          <Link
            href="/demos/dental/crm"
            className="shrink-0 px-4 py-2 rounded-xl bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold text-xs transition-colors flex items-center gap-1.5"
          >
            <span>Open Clinic CRM</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
